// src/components/Login.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_BASE_URL ;
console.log("API Base URL:", API);

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
console.log("Google Client ID:", GOOGLE_CLIENT_ID);

/* ── Google Identity Services loader ── */
function useGoogleAuth(onSuccess) {
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: onSuccess,
      });
      window.google.accounts.id.renderButton(
        document.getElementById("google-signin-btn"),
        {
          type: "standard",
          theme: "filled_black",
          size: "large",
          text: "signin_with",
          shape: "rectangular",
          width: "100%",
        }
      );
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [onSuccess]);
}

function Login() {
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [showPassword, setShowPw]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [googleLoading, setGoogleL] = useState(false);
  const [error, setError]           = useState("");
  const navigate = useNavigate();

  /* Google callback */
  const handleGoogleResponse = React.useCallback(async ({ credential }) => {
    setError("");
    setGoogleL(true);
    try {
      const res = await axios.post(
        `/auth/google`,
        { token: credential },
        { withCredentials: true }
      );
      if (res.data.success) navigate(res.data.redirectTo || "/shopping-cart");
    } catch (err) {
      setError(err.response?.data?.message || "Google sign-in failed. Please try again.");
    } finally {
      setGoogleL(false);
    }
  }, [navigate]);

  useGoogleAuth(handleGoogleResponse);

  /* Email / password login */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(
        `/login`,
        { email, password },
        { withCredentials: true }   // sends/receives the httpOnly cookie
      );
      navigate("/shopping-cart");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root {
          --gold: #C9A84C;
          --gold-light: #E8C97A;
          --dark: #0A0A0A;
        }

        .auth-root {
          font-family: 'Jost', sans-serif;
          background: var(--dark);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 6rem 1.5rem 3rem;
        }
        .auth-root::before {
          content: '';
          position: absolute;
          top: -20%; left: 50%;
          transform: translateX(-50%);
          width: 700px; height: 700px;
          background: radial-gradient(ellipse, rgba(201,168,76,0.06) 0%, transparent 65%);
          pointer-events: none;
        }
        .auth-root::after {
          content: '';
          position: absolute;
          bottom: -10%; right: -10%;
          width: 400px; height: 400px;
          background: radial-gradient(ellipse, rgba(201,168,76,0.04) 0%, transparent 65%);
          pointer-events: none;
        }

        .auth-corner {
          position: absolute;
          width: 80px; height: 80px;
          pointer-events: none;
        }
        .auth-corner.tl { top: 80px; left: 24px; border-top: 1px solid rgba(201,168,76,0.25); border-left: 1px solid rgba(201,168,76,0.25); }
        .auth-corner.br { bottom: 24px; right: 24px; border-bottom: 1px solid rgba(201,168,76,0.25); border-right: 1px solid rgba(201,168,76,0.25); }

        .auth-card {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 460px;
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(201,168,76,0.2);
          border-radius: 4px;
          padding: 3rem 2.5rem 2.5rem;
          animation: authFadeUp 0.7s ease both;
        }
        @keyframes authFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .auth-card::before {
          content: '';
          position: absolute;
          top: 0; left: 10%; right: 10%;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--gold), transparent);
        }

        .auth-eyebrow {
          font-size: 0.62rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          text-align: center;
          margin-bottom: 0.6rem;
        }
        .auth-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.6rem;
          font-weight: 700;
          color: #fff;
          text-align: center;
          line-height: 1.1;
          margin-bottom: 0.3rem;
        }
        .auth-title em { font-style: italic; color: var(--gold-light); }
        .auth-divider {
          width: 40px; height: 1px;
          background: var(--gold);
          margin: 1.2rem auto 1.8rem;
          opacity: 0.7;
        }
        .auth-subtitle {
          font-size: 0.83rem;
          color: rgba(255,255,255,0.4);
          text-align: center;
          margin-bottom: 2rem;
          font-weight: 300;
          line-height: 1.7;
        }

        /* Google button wrapper */
        .google-btn-wrap {
          margin-bottom: 0.5rem;
          display: flex;
          justify-content: center;
        }
        .google-btn-wrap > div {
          width: 100% !important;
        }
        /* Override Google button width */
        #google-signin-btn iframe {
          width: 100% !important;
        }

        .auth-error {
          background: rgba(220,53,69,0.1);
          border: 1px solid rgba(220,53,69,0.3);
          border-radius: 3px;
          padding: 0.75rem 1rem;
          font-size: 0.82rem;
          color: #f8908a;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .auth-or {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin: 1.6rem 0;
        }
        .auth-or::before, .auth-or::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.07);
        }
        .auth-or span {
          font-size: 0.65rem;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.2);
        }

        .auth-field { margin-bottom: 1.4rem; }
        .auth-label {
          display: block;
          font-size: 0.65rem;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          margin-bottom: 0.55rem;
          font-weight: 500;
        }
        .auth-input-wrap { position: relative; }
        .auth-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(201,168,76,0.18);
          border-radius: 3px;
          padding: 0.85rem 1rem;
          color: #fff;
          font-family: 'Jost', sans-serif;
          font-size: 0.9rem;
          font-weight: 300;
          outline: none;
          transition: border-color 0.25s, background 0.25s;
          -webkit-appearance: none;
        }
        .auth-input::placeholder { color: rgba(255,255,255,0.2); }
        .auth-input:focus {
          border-color: rgba(201,168,76,0.55);
          background: rgba(201,168,76,0.04);
        }
        .auth-input.has-toggle { padding-right: 3.5rem; }

        .auth-toggle {
          position: absolute;
          right: 1rem; top: 50%;
          transform: translateY(-50%);
          background: none; border: none;
          color: rgba(255,255,255,0.3);
          cursor: pointer; padding: 0;
          font-size: 0.72rem;
          letter-spacing: 1px;
          text-transform: uppercase;
          font-family: 'Jost', sans-serif;
          transition: color 0.2s;
        }
        .auth-toggle:hover { color: var(--gold); }

        .auth-forgot {
          display: block;
          text-align: right;
          font-size: 0.72rem;
          color: rgba(255,255,255,0.3);
          text-decoration: none;
          margin-top: 0.45rem;
          transition: color 0.2s;
          letter-spacing: 0.5px;
        }
        .auth-forgot:hover { color: var(--gold); }

        .auth-submit {
          width: 100%;
          background: var(--gold);
          border: none;
          border-radius: 3px;
          padding: 0.95rem;
          color: #0A0A0A;
          font-family: 'Jost', sans-serif;
          font-weight: 600;
          font-size: 0.82rem;
          letter-spacing: 3px;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.3s, transform 0.2s, opacity 0.2s;
          margin-top: 1.8rem;
          position: relative;
          overflow: hidden;
        }
        .auth-submit::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent);
          transform: translateX(-100%);
          transition: transform 0.55s;
        }
        .auth-submit:hover::after { transform: translateX(100%); }
        .auth-submit:hover { background: var(--gold-light); transform: translateY(-1px); }
        .auth-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

        .auth-footer {
          text-align: center;
          margin-top: 2rem;
          font-size: 0.8rem;
          color: rgba(255,255,255,0.3);
        }
        .auth-footer a {
          color: var(--gold);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }
        .auth-footer a:hover { color: var(--gold-light); }

        .auth-spinner {
          display: inline-block;
          width: 14px; height: 14px;
          border: 2px solid rgba(10,10,10,0.3);
          border-top-color: #0A0A0A;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          vertical-align: middle;
          margin-right: 0.5rem;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Fallback Google button (if GIS not loaded) */
        .google-fallback {
          display: flex; align-items: center; justify-content: center; gap: 0.65rem;
          width: 100%; padding: 0.85rem 1rem;
          background: #fff; border: none; border-radius: 3px;
          color: #3c4043; font-family: 'Jost', sans-serif;
          font-size: 0.85rem; font-weight: 500;
          cursor: pointer; transition: background 0.2s, box-shadow 0.2s;
          letter-spacing: 0.3px;
        }
        .google-fallback:hover { background: #f8f9fa; box-shadow: 0 1px 6px rgba(0,0,0,0.3); }
        .google-fallback:disabled { opacity: 0.55; cursor: not-allowed; }
      `}</style>

      <div className="auth-root">
        <div className="auth-corner tl" />
        <div className="auth-corner br" />

        <div className="auth-card">
          <p className="auth-eyebrow">Nairobi's Ultimate Experience</p>
          <h1 className="auth-title">Welcome <em>Back</em></h1>
          <div className="auth-divider" />
          <p className="auth-subtitle">Sign in to manage your bookings and adventures</p>

          {error && (
            <div className="auth-error"><span>✕</span> {error}</div>
          )}

          {/* ── Google Sign-In ── */}
          <div className="google-btn-wrap">
            {/* GIS renders here; shown once script loads */}
            <div id="google-signin-btn" style={{ width: "100%" }} />
          </div>
          {/* Fallback shown while GIS loads or if no client ID */}
          {!process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <button
              className="google-fallback"
              disabled
              title="Set REACT_APP_GOOGLE_CLIENT_ID to enable"
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>
          )}

          <div className="auth-or"><span>or</span></div>

          {/* ── Email / Password ── */}
          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label className="auth-label" htmlFor="login-email">Email Address</label>
              <div className="auth-input-wrap">
                <input
                  id="login-email"
                  type="email"
                  className="auth-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="login-password">Password</label>
              <div className="auth-input-wrap">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  className="auth-input has-toggle"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button type="button" className="auth-toggle" onClick={() => setShowPw(v => !v)}>
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <Link to="/forgot-password" className="auth-forgot">Forgot password?</Link>
            </div>

            <button type="submit" className="auth-submit" disabled={loading || googleLoading}>
              {loading && <span className="auth-spinner" />}
              {loading ? "Signing In…" : "Sign In"}
            </button>
          </form>

          <div className="auth-footer">
            Don't have an account? <Link to="/register">Create one →</Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
